import { Suspense, useState } from 'react'

import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'

import { createDialog } from '@/components/create-dialog'
import type { DialogActionValue, DialogComponentProps } from '@/components/create-dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { DialogSelecionarArquivoContent } from './dialog-selecionar-arquivo-content'

interface DialogSelecionarArquivoOptions {
  idExterno: number
  nomeEmpreendimento: string
}

interface DialogSelecionarArquivoResult {
  arquivoSelecionadoId: string
  nomeArquivo: string
}

interface ErrorFallbackProps {
  resetErrorBoundary: () => void
  onCancel: () => void
}

const ErrorFallback = ({ resetErrorBoundary, onCancel }: ErrorFallbackProps) => {
  const { t } = useTranslation('empreendimentos')

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>{t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.erro_titulo')}</AlertDialogTitle>
        <AlertDialogDescription>
          {t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.erro_descricao')}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <Button variant="outline" onClick={onCancel}>
          {t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.botao_cancelar')}
        </Button>
        <Button onClick={resetErrorBoundary}>
          {t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.botao_tentar_novamente')}
        </Button>
      </AlertDialogFooter>
    </>
  )
}

const DialogSelecionarArquivoComponent = ({
  onAction,
  options: { idExterno },
}: DialogComponentProps<DialogSelecionarArquivoOptions, DialogSelecionarArquivoResult>) => {
  const { t } = useTranslation('empreendimentos')
  const [open, setOpen] = useState(true)
  const [arquivoSelecionado, setArquivoSelecionado] = useState<{ url: string; nome: string }>({ url: '', nome: '' })
  const handleAction = async (value: DialogActionValue) => {
    setOpen(false)
    onAction(value, {
      arquivoSelecionadoId: arquivoSelecionado.url,
      nomeArquivo: arquivoSelecionado.nome,
    })
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      onAction('dismiss')
    }
  }

  const handleCancel = () => handleAction('cancel')
  const renderErrorFallback = (props: FallbackProps) => <ErrorFallback {...props} onCancel={handleCancel} />

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="gap-6">
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary FallbackComponent={renderErrorFallback} onReset={reset}>
              <Suspense
                fallback={
                  <>
                    <AlertDialogHeader className="space-y-2">
                      <AlertDialogTitle className="text-xl tracking-wider">
                        {t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.carregando_titulo')}
                      </AlertDialogTitle>
                      <AlertDialogDescription className="sr-only">
                        {t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.carregando_descricao')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="-mx-6 space-y-3 border-t border-b border-neutral-100 px-6 py-4">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                    <AlertDialogFooter>
                      <Skeleton className="h-9 w-20" />
                      <Skeleton className="h-9 w-24" />
                    </AlertDialogFooter>
                  </>
                }
              >
                <DialogSelecionarArquivoContent
                  idExterno={idExterno}
                  arquivoSelecionado={arquivoSelecionado}
                  onArquivoSelecionadoChange={setArquivoSelecionado}
                  onAction={handleAction}
                />
              </Suspense>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export const DialogSelecionarArquivo = createDialog(DialogSelecionarArquivoComponent)
